import {QueryErrorResetBoundary} from '@tanstack/react-query';
import {NextRouter, useRouter} from 'next/router';
import {Component, ErrorInfo, ReactNode} from 'react';
import {requestContext} from '../common/requestContext';
import {ApiStatusCodeVo} from '../common/shared/api/nicolas/vo/enums/ApiStatusCodeVo.enum';
import {Maintenance} from '../domain/maintenance/presentational/Maintenance';
import {SHOW_NETWORK_ERROR_LIST} from './constants';
import {getSerializedPagewebError} from './getSerializedPagewebError.function';
import {NetworkError} from './NetworkError';
import {PagewebErrorType} from './PagewebErrorType.enum';
import {SerializedPagewebError} from './SerializedPagewebError.interface';
import {UnknownError} from './UnknownError';

interface Props {
  reset: VoidFunction;
  router: NextRouter;
  children: ReactNode;
}

interface State {
  shouldHandleError: boolean;
  serializedPagewebError?: SerializedPagewebError;
}

class _GlobalErrorBoundary extends Component<Props, State> {
  public state: State = {
    shouldHandleError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      shouldHandleError: true,
      serializedPagewebError: getSerializedPagewebError(error),
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('global error:', error, errorInfo);
  }

  clearHandleError = () => {
    if (this.state.shouldHandleError) {
      this.setState({shouldHandleError: false});
    }
  };

  public componentDidMount() {
    this.props.router.events.on('routeChangeComplete', this.clearHandleError);
  }

  public componentWillUnmount() {
    this.props.router.events.off('routeChangeComplete', this.clearHandleError);
  }

  public render() {
    if (!this.state.shouldHandleError) {
      return this.props.children;
    }

    if (
      this.state.serializedPagewebError?.type &&
      SHOW_NETWORK_ERROR_LIST.includes(this.state.serializedPagewebError?.type)
    ) {
      return (
        <NetworkError
          isMobile={requestContext.requestInfo.display.isMobile}
          onClickRetry={() => {
            this.props.reset();
            this.setState({shouldHandleError: false});
          }}
        />
      );
    } else if (
      (this.state.serializedPagewebError?.type ===
        PagewebErrorType.NicolasApiStatusError ||
        this.state.serializedPagewebError?.type ===
          PagewebErrorType.KarinaApiStatusError) &&
      this.state.serializedPagewebError?.resultCode ===
        ApiStatusCodeVo.ServerMaintenance
    ) {
      return (
        <Maintenance isMobile={requestContext.requestInfo.display.isMobile} />
      );
    } else {
      return (
        <UnknownError
          isMobile={requestContext.requestInfo.display.isMobile}
          onClickRetry={() => {
            this.props.reset();
            this.setState({shouldHandleError: false});
          }}
        />
      );
    }
  }
}

export function GlobalErrorBoundary({children}: {children: ReactNode}) {
  const router = useRouter();

  return (
    <QueryErrorResetBoundary>
      {({reset}) => (
        <_GlobalErrorBoundary router={router} reset={reset}>
          {children}
        </_GlobalErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
}
